import { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import StorageIcon from '@material-ui/icons/Storage';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { dbDumpPermission } from '../../permissions';

interface EnvConfig {
  key: string;
  displayName: string;
  color: string;
}

interface DatabaseEntry {
  pod: string;
  engine: string;
  database: string;
}

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.06em',
    },
  },
  table: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
    },
    '& td': {
      padding: theme.spacing(1.5, 2),
    },
  },
  dbName: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  engineChip: {
    fontSize: '0.65rem',
    height: 20,
    fontWeight: 600,
  },
  successText: {
    color: '#4caf50',
    fontSize: '0.75rem',
    marginTop: 4,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: 4,
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}));

export const DatabaseDumpContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const annotations = entity.metadata.annotations ?? {};
  const hasDatabase = annotations['fondative.io/has-database'] === 'true';
  const { allowed: canDump } = usePermission({ permission: dbDumpPermission });

  const [activeTab, setActiveTab] = useState(0);
  const [envs, setEnvs] = useState<EnvConfig[]>([]);
  const [databases, setDatabases] = useState<Record<string, DatabaseEntry[]>>({});
  const [loadingDbs, setLoadingDbs] = useState<Record<string, boolean>>({});
  const [dumpingDb, setDumpingDb] = useState<string | null>(null);
  const [dumpError, setDumpError] = useState<string | null>(null);
  const [dumpResult, setDumpResult] = useState<{ downloadUrl?: string; summary?: string } | null>(null);

  useEffect(() => {
    try {
      const arr = configApi.getConfigArray('fondative.environments');
      setEnvs(
        arr.map(c => ({
          key: c.getString('key'),
          displayName: c.getString('displayName'),
          color: c.getOptionalString('color') ?? '#1976d2',
        })),
      );
    } catch {
      setEnvs([]);
    }
  }, [configApi]);

  // Fetch databases when tab changes
  useEffect(() => {
    if (!envs.length || !hasDatabase) return;
    const envKey = envs[activeTab]?.key;
    if (!envKey || databases[envKey] || loadingDbs[envKey]) return;
    setLoadingDbs(prev => ({ ...prev, [envKey]: true }));
    fetchApi
      .fetch(`${backendBaseUrl}/api/db-dump/databases?environment=${envKey}`)
      .then(async res => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error?.message ?? `HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data: DatabaseEntry[]) => {
        setDatabases(prev => ({ ...prev, [envKey]: data }));
        setLoadingDbs(prev => ({ ...prev, [envKey]: false }));
      })
      .catch(() => {
        setDatabases(prev => ({ ...prev, [envKey]: [] }));
        setLoadingDbs(prev => ({ ...prev, [envKey]: false }));
      });
  }, [envs, activeTab, hasDatabase, databases, loadingDbs, backendBaseUrl, fetchApi]);

  const handleDump = useCallback(
    async (envKey: string, database?: string) => {
      const dumpId = `${envKey}-${database ?? 'all'}`;
      setDumpingDb(dumpId);
      setDumpError(null);
      setDumpResult(null);
      try {
        const res = await fetchApi.fetch(`${backendBaseUrl}/api/db-dump/dump`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ environment: envKey, database }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error?.message ?? `HTTP ${res.status}`);
        }
        const result = await res.json();
        setDumpResult({
          downloadUrl: result.downloadUrl,
          summary: result.summary,
        });
      } catch (err: any) {
        setDumpError(err.message);
      } finally {
        setDumpingDb(null);
      }
    },
    [backendBaseUrl, fetchApi],
  );

  if (!hasDatabase) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">
          Ajoutez l'annotation <code>fondative.io/has-database: "true"</code> pour activer le dump DB.
        </Typography>
      </Box>
    );
  }

  if (!envs.length) return null;

  const currentEnv = envs[activeTab];
  const currentDbs = databases[currentEnv.key] ?? [];
  const isLoading = loadingDbs[currentEnv.key] ?? false;

  return (
    <Card>
      <Tabs
        value={activeTab}
        onChange={(_, v) => {
          setActiveTab(v);
          setDumpError(null);
          setDumpResult(null);
        }}
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
      >
        {envs.map(env => (
          <Tab key={env.key} label={env.displayName} style={{ color: env.color }} />
        ))}
      </Tabs>
      <CardContent style={{ padding: 0 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : currentDbs.length === 0 ? (
          <Box className={classes.empty}>
            <Typography color="textSecondary">
              Aucune base de données détectée dans <strong>{currentEnv.displayName}</strong>
            </Typography>
          </Box>
        ) : (
          <>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Base de données</TableCell>
                  <TableCell>Engine</TableCell>
                  <TableCell>Pod</TableCell>
                  <TableCell align="right">Dump</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentDbs.map(db => {
                  const dumpId = `${currentEnv.key}-${db.database}`;
                  const isDumping = dumpingDb === dumpId;
                  return (
                    <TableRow key={dumpId} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                          <StorageIcon fontSize="small" color="action" />
                          <Typography className={classes.dbName}>{db.database}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={db.engine}
                          className={classes.engineChip}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {db.pod}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {canDump ? (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!!dumpingDb}
                            startIcon={
                              isDumping
                                ? <CircularProgress size={14} color="inherit" />
                                : <GetAppIcon fontSize="small" />
                            }
                            onClick={() => handleDump(currentEnv.key, db.database)}
                            style={{ textTransform: 'none' }}
                          >
                            {isDumping ? 'Dump...' : 'Dump anonymisé'}
                          </Button>
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            Droits insuffisants
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {dumpError && (
              <Box px={2} pb={2}>
                <Typography className={classes.errorText}>{dumpError}</Typography>
              </Box>
            )}
            {dumpResult?.summary && (
              <Box px={2} pb={1}>
                <Typography variant="body2">{dumpResult.summary}</Typography>
              </Box>
            )}
            {dumpResult?.downloadUrl && (
              <Box px={2} pb={2}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<GetAppIcon />}
                  href={dumpResult.downloadUrl}
                  style={{ textTransform: 'none' }}
                >
                  Télécharger le dump
                </Button>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
